import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AlternativeSummary } from '../types';
import AlternativeJourneyChip from './AlternativeJourneyChip';

// Horizontal scroller of AlternativeJourneyChips + a trailing dashed "＋ New"
// chip. Selecting a chip drives the RouteFocus block. The focused chip is
// auto-centered, edge arrows + an always-visible scrollbar signal overflow.
// See the design design-handoff.md: docs/prototypes/my-trips/design-handoff.md §C.2 step 4.

export interface AlternativesSwitcherProps {
  alternatives: AlternativeSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Open a chip's journey detail (fired when an already-selected chip is clicked). */
  onOpen: (id: string) => void;
  /** Delete an alternative from a chip's kebab menu (parent confirms +
   *  removes). Optional so read-only surfaces (shared views) drop the row. */
  onDelete?: (id: string) => void;
  /** Chip kebab → Rename (parent opens the name dialog). */
  onRename?: (id: string) => void;
  /** Chip kebab → Set as main. */
  onSetMain?: (id: string) => void;
  /** Chip kebab → Create copy (parent opens the name dialog). */
  onCopy?: (id: string) => void;
  /** Label for the chips' copy row (shared views say "Save a copy"). */
  copyLabel?: string;
  /** Chip kebab → Delete other alternatives (parent confirms). */
  onDeleteOthers?: (id: string) => void;
  /** Hover preview passthrough: alt id on chip mouse enter, null on leave. */
  onHoverChange?: (id: string | null) => void;
  // Optional: when omitted, the trailing "＋ New" chip is hidden (the compact
  // AlternativeGenerator below the list is the add affordance instead).
  onAdd?: () => void;
}

// Persistent (non-overlay) scrollbar so overflow is always discoverable — macOS
// overlay scrollbars otherwise auto-hide. Scoped to the .alt-scroller class.
const SCROLLBAR_CSS = `
.alt-scroller::-webkit-scrollbar { height: 8px; -webkit-appearance: none; }
.alt-scroller::-webkit-scrollbar-thumb { background: rgba(20,40,30,0.22); border-radius: 999px; }
.alt-scroller::-webkit-scrollbar-thumb:hover { background: rgba(20,40,30,0.34); }
.alt-scroller::-webkit-scrollbar-track { background: transparent; }
`;

const AddAlternativeChip: React.FC<{ onAdd: () => void }> = ({ onAdd }) => (
  <button
    onClick={onAdd}
    style={{
      flex: '0 0 auto',
      width: 96,
      minHeight: 96,
      borderRadius: 16,
      border: '1.5px dashed rgba(20,40,30,0.22)',
      background: 'transparent',
      color: '#157a55',
      fontWeight: 600,
      fontSize: 13,
      cursor: 'pointer',
    }}
  >
    ＋ New
  </button>
);

// Round edge affordance, same white-circle-with-chevron family as TripDetailNav's
// back button. Shown only when there is more content that direction.
const ScrollArrow: React.FC<{ side: 'left' | 'right'; onClick: () => void }> = ({ side, onClick }) => (
  <button
    type="button"
    aria-label={side === 'left' ? 'Scroll left' : 'Scroll right'}
    onClick={onClick}
    style={{
      position: 'absolute',
      top: 'calc(50% - 7px)', // nudge up for the bottom scrollbar/padding
      transform: 'translateY(-50%)',
      left: side === 'left' ? 2 : undefined,
      right: side === 'right' ? 2 : undefined,
      width: 32,
      height: 32,
      borderRadius: 999,
      border: '1px solid rgba(20,40,30,0.10)',
      background: '#ffffff',
      boxShadow: '0 2px 8px rgba(20,40,30,0.20)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: '#157a55',
      zIndex: 2,
      padding: 0,
    }}
  >
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d={side === 'left' ? 'M9 2 L4 7 L9 12' : 'M5 2 L10 7 L5 12'}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

const AlternativesSwitcher: React.FC<AlternativesSwitcherProps> = ({
  alternatives,
  selectedId,
  onSelect,
  onOpen,
  onDelete,
  onRename,
  onSetMain,
  onCopy,
  copyLabel,
  onDeleteOthers,
  onHoverChange,
  onAdd,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const s = scrollerRef.current;
    if (!s) return;
    const max = s.scrollWidth - s.clientWidth;
    setCanLeft(s.scrollLeft > 1);
    setCanRight(s.scrollLeft < max - 1);
  }, []);

  // Keep the focused alternative centered and fully visible in the scroller.
  // We set this scroller's scrollLeft DIRECTLY rather than calling
  // chip.scrollIntoView({inline:'center'}): scrollIntoView also scrolls ancestor
  // containers / the page and, on the initial production load, intermittently
  // left a far-right focus off-screen (it under-scrolls when a large jump is
  // needed). A direct, element-scoped scrollLeft is deterministic. useLayoutEffect
  // measures after the DOM is committed and applies before paint, so no flash.
  useLayoutEffect(() => {
    if (!selectedId) return;
    const scroller = scrollerRef.current;
    if (!scroller || !scroller.clientWidth) return;
    // Select the focused chip by its aria-selected button — NOT by child index:
    // each chip renders a sibling <style> node, so scroller.children interleaves
    // zero-width <style> elements with the buttons.
    const chip = scroller.querySelector('[role="tab"][aria-selected="true"]') as HTMLElement | null;
    if (!chip || !chip.clientWidth) return;
    // Chip start within the scroll content (independent of the current scroll),
    // then center it. The browser clamps scrollLeft to its valid range, so the
    // first/last chips land flush to their edge while staying fully visible.
    const sRect = scroller.getBoundingClientRect();
    const cRect = chip.getBoundingClientRect();
    const chipStart = (cRect.left - sRect.left) + scroller.scrollLeft;
    scroller.scrollLeft = Math.max(0, chipStart - (scroller.clientWidth - cRect.width) / 2);
    updateArrows();
  }, [selectedId, alternatives, updateArrows]);

  // Recompute arrow visibility when the set changes or the window resizes.
  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [alternatives, updateArrows]);

  const nudge = useCallback((dir: 1 | -1) => {
    const s = scrollerRef.current;
    if (!s) return;
    s.scrollBy({ left: dir * Math.round(s.clientWidth * 0.6), behavior: 'smooth' });
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      <style>{SCROLLBAR_CSS}</style>
      <div
        ref={scrollerRef}
        className="alt-scroller"
        role="tablist"
        aria-label="Saved alternatives"
        onScroll={updateArrows}
        style={{
          display: 'flex',
          gap: 10,
          overflowX: 'auto',
          paddingBottom: 6,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {alternatives.map((alt) => (
          <AlternativeJourneyChip key={alt.id} alt={alt} selected={alt.id === selectedId} onSelect={onSelect} onOpen={onOpen} onDelete={onDelete} onRename={onRename} onCopy={onCopy} copyLabel={copyLabel} onSetMain={onSetMain} onDeleteOthers={alternatives.length > 1 ? onDeleteOthers : undefined} onHoverChange={onHoverChange} />
        ))}
        {onAdd && <AddAlternativeChip onAdd={onAdd} />}
      </div>
      {canLeft && <ScrollArrow side="left" onClick={() => nudge(-1)} />}
      {canRight && <ScrollArrow side="right" onClick={() => nudge(1)} />}
    </div>
  );
};

export default AlternativesSwitcher;
